import LZString from 'lz-string';

// Types
import { AuthType } from '../types/auth';
import { clearOfflineSession, saveOfflineSession } from './offlineSession';

// 🔧 DEBUG: Desactivar compresión temporalmente para debugging
// Cambiar a true para activar compresión en producción
const ENABLE_COMPRESSION = true;

/**
 * Lee un valor de `sessionStorage` sea cual sea el formato en que se escribió.
 *
 * El build comprime y el dev server no (ver `compressSessionStorage`), así que
 * un mismo almacén puede contener las dos formas: al desplegar una versión
 * nueva sobre una pestaña que ya tenía datos, o al abrir la app construida
 * sobre lo que dejó una sesión anterior. Antes, encontrarse el formato
 * "contrario" reventaba con `Unexpected token '@'` y la pantalla de Error
 * general — un fallo duro por un dato que en realidad era legible.
 *
 * El discriminante es fiable por construcción: **todo lo que guardamos aquí es
 * JSON**, y una cadena LZ-UTF16 nunca parsea como JSON. Así que si parsea, ya
 * estaba en claro y se devuelve tal cual.
 *
 * 🔴 Vale en CUALQUIER modo, también en el dev server. Antes había aquí un
 * `if (!import.meta.env.PROD) return data` que devolvía lo comprimido sin
 * tocar: con una build de producción servida antes en el mismo origen (su
 * service worker sobrevive al dev server), el dev se encontró la sesión y
 * Redux comprimidos, no pudo leer ninguno y la app cayó en la página de error
 * (Sentry ROIS-FRONT-C6/C7/C8, 2026-10-08). Escribir en claro en dev sigue
 * igual; solo la LECTURA tolera los dos formatos.
 */
export const decompressSessionStorage = (data: string) => {
  if (!ENABLE_COMPRESSION) {
    return data;
  }
  try {
    JSON.parse(data);
    return data; // estaba en claro
  } catch {
    return LZString.decompressFromUTF16(data);
  }
};

export const compressSessionStorage = (data: string) => {
  if (!ENABLE_COMPRESSION) {
    return data;
  }
  return import.meta.env.PROD ? LZString.compressToUTF16(data) : data;
};

export const getSessionStorageUser = () => {
  const user = sessionStorage.getItem('user');
  if (!user) return null;
  try {
    // Defensa contra sesiones envenenadas de versiones anteriores (el `"null"`
    // comprimido de arriba) y contra un valor que no se pueda descomprimir:
    // `LZString` devuelve `null` y `JSON.parse(null)` también, así que sin esto
    // el llamante recibe `null` disfrazado de sesión válida.
    return JSON.parse(decompressSessionStorage(user) ?? 'null');
  } catch {
    return null;
  }
};

export const setSessionStorageUser = (user: AuthType | null) => {
  // Cerrar sesión pasa `null`. Guardarlo comprimido dejaba en `sessionStorage`
  // una cadena TRUTHY que al descomprimir vale `"null"` y al parsear vuelve a
  // ser `null` — una sesión envenenada que revienta a todo el que haga
  // `if (value) { ...JSON.parse(...) }` y luego lea una propiedad, como
  // `rolesAuthRoutesCheck` (menú de navegación) o `useSessionStorage`.
  //
  // En web pasaba desapercibido porque cerrar la pestaña limpiaba el rastro.
  // En la app nativa, con la sesión espejada a `localStorage`, ese `null`
  // sobrevive a cerrar la app y deja el arranque roto PARA SIEMPRE: sin menú y
  // con "Empty AccessToken" en cada petición.
  if (user === null) {
    sessionStorage.removeItem('user');
    // Cerrar sesión debe llevarse TAMBIÉN la copia para reapertura sin
    // cobertura. Si no, quedaría en `localStorage` —que sobrevive a todo— y el
    // siguiente operario de una tablet compartida podría reabrir sin red y
    // entrar con la sesión del anterior.
    clearOfflineSession();
    // Lo propio y lo ajeno de las colas dependen de quién tiene la sesión: el
    // banner recuenta (spec 2026-10-01 § 6.3).
    window.dispatchEvent(new CustomEvent('session-changed'));
    return;
  }
  sessionStorage.setItem('user', compressSessionStorage(JSON.stringify(user)));
  // Refrescar la copia con los tokens al día. Conserva la caducidad original:
  // la ventana se cuenta desde el login, no desde la última actividad.
  saveOfflineSession(user);
  // Lo propio y lo ajeno de las colas dependen de quién tiene la sesión: el
  // banner recuenta (spec 2026-10-01 § 6.3).
  window.dispatchEvent(new CustomEvent('session-changed'));
};
